/**
 * shared/station_services.ts — CLIENT MIRROR of server/src/lib/station_services.ts (the server copy is authoritative;
 * keep them byte-identical below the headers — `verify:station-services` diffs them). Space campaign K (2026-10-01):
 * station services by role, binding service quotes, the outage verdicts, the wreck berth, the muster, the depot's
 * capped resupply, the bounded firm planner and the treaty's one permission. Pure.
 */
import { stationRoleOf, STATION_ROLE_DEFS, type StationRole } from './space_stations';
import { FIRM_DEFS, HULL_ORE_U, HULLS_MAX, SHIPMENT_LOT_U, MINER_FAMILY, AMBUSH_P_UNESCORTED } from './space_economy';
import { CEASEFIRE_CYCLES } from './space_ops';

export const STATION_SERVICES_FLAG = 'station_services_v1';
/** The repair service's version (stamped on every quote and job — a station repair is a versioned service, never a
 *  silent replacement of the old 25-SOV acknowledgement, which stays the flag-off path). */
export const REPAIR_SERVICE_VERSION = 2;
export const RECOVERY_SERVICE_VERSION = 1;

// ── THE ROLES' SERVICES ──────────────────────────────────────────────────────────────────────────────────────────
export interface RoleService {
  /** Repair cradles: jobs that may work at once. */
  berths: number;
  /** Labour per part (credits) — the station's crew. */
  labourPerPartSov: number;
  /** A repair restores the hull up to this share of its fitted maximum (a frontier outpost's limited repair). */
  restoreCapPct: number;
  /** The platform relief stock (parts) where no firm supplies them: cap and hourly restock. */
  reliefCap: number;
  reliefPerHour: number;
  /** The station refine line's yield (the refinery's difference). */
  refineEff: number;
  /** The Exchange's depth divisor (the trade hub's difference: a lot moves the mark 1/scale as far). */
  depthScale: number;
}
export const ROLE_SERVICE: Readonly<Record<StationRole, RoleService>> = {
  trade_hub: { berths: 2, labourPerPartSov: 3, restoreCapPct: 100, reliefCap: 20, reliefPerHour: 8, refineEff: 0.35, depthScale: 2 },
  yard: { berths: 4, labourPerPartSov: 2, restoreCapPct: 100, reliefCap: 20, reliefPerHour: 8, refineEff: 0.35, depthScale: 1 },
  refinery: { berths: 2, labourPerPartSov: 3, restoreCapPct: 100, reliefCap: 20, reliefPerHour: 8, refineEff: 0.45, depthScale: 1 },
  research_post: { berths: 2, labourPerPartSov: 3, restoreCapPct: 100, reliefCap: 20, reliefPerHour: 8, refineEff: 0.35, depthScale: 1 },
  frontier_outpost: { berths: 1, labourPerPartSov: 3, restoreCapPct: 60, reliefCap: 8, reliefPerHour: 3, refineEff: 0.35, depthScale: 1 },
  salvage_yard: { berths: 2, labourPerPartSov: 3, restoreCapPct: 100, reliefCap: 20, reliefPerHour: 8, refineEff: 0.35, depthScale: 1 },
  military_fort: { berths: 3, labourPerPartSov: 3, restoreCapPct: 100, reliefCap: 20, reliefPerHour: 8, refineEff: 0.35, depthScale: 1 },
};
/** The base refine line (= space_industry REFINE_STATION_EFF) — every station off the flag. */
export const BASE_REFINE_EFF = 0.35;
export function roleService(stationKey: string): RoleService { return ROLE_SERVICE[stationRoleOf(stationKey)]; }
export function roleLabel(stationKey: string): string { return STATION_ROLE_DEFS[stationRoleOf(stationKey)].label; }
/** The refine line's yield at a docked station (on = the role's, off = the base line). */
export function refineEffAt(stationKey: string, on: boolean): number { return on ? roleService(stationKey).refineEff : BASE_REFINE_EFF; }
/** The depth units the Exchange prices with at a station: a trade hub absorbs a lot as if it were 1/scale its size. */
export function depthUnitsAt(stationKey: string, units: number, on: boolean): number {
  if (!on) return units;
  const s = roleService(stationKey).depthScale;
  return s > 1 ? Math.floor(units / s) : units;
}

// ── THE FIT (what the server can bound a claimed repair by — the hp / missiles / counters part of shipStats) ──────
/** Client `CHASSIS` hull integrity (fleet.ts) — verify:station-services pins it against the client's shipStats. */
export const CHASSIS_HP: Readonly<Record<string, number>> = { ship_lancer: 100, ship_talon: 70, ship_courier: 120, ship_bulwark: 220, ship_sovereign: 380,
  ship_wren: 60, ship_packet: 80, ship_pathfinder: 110, ship_kestrel: 45, ship_warden: 160,
  ship_prospector: 130, ship_gleaner: 180, ship_harrier: 110, ship_vesper: 90, ship_farstrider: 160,
  ship_arbalest: 150, ship_drover: 180, ship_tender: 240, ship_delver: 260, ship_laurel: 190,
  ship_rime: 150, ship_ironclad: 320, ship_bastion: 200, ship_argosy: 300, ship_reclaimer: 320,
};
export interface FitComp { slot: string; itemKey: string; quality: number }
export function fitMaxOf(chassisKey: string, comps: readonly FitComp[]): { hp: number; missiles: number; counters: number } {
  const q = (slot: string): number => comps.find((c) => c.slot === slot)?.quality ?? -1;
  const qm = (v: number, span: number): number => 1 + Math.max(0, Math.min(1000, v)) / 1000 * span;
  const base = CHASSIS_HP[chassisKey] ?? CHASSIS_HP.ship_lancer;
  return {
    hp: Math.round(base * (q('armor') >= 0 ? qm(q('armor'), 0.6) : 1)),
    missiles: q('missile') >= 0 ? 2 + Math.floor(q('missile') / 250) : 0,
    counters: q('counter') >= 0 ? 2 + Math.floor(q('counter') / 300) : 0,
  };
}
/** The fit's revision: a quote is for THIS fit — a refit after the quote ends the job (its parts may no longer fit). */
export function fitRevOf(comps: readonly FitComp[]): string {
  const s = comps.map((c) => `${c.slot}:${c.itemKey}:${c.quality}`).sort().join('|');
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(16).padStart(8, '0');
}

// ── THE REPAIR QUOTE ─────────────────────────────────────────────────────────────────────────────────────────────
/** A part is one unit of ferrite ore cut into plating: it restores 10 hull (the report's kit size, §7.3). */
export const PART_FAMILY = MINER_FAMILY;
export const PART_ORE_U = 1;
export const PART_HP = 10;
/** A part also restores 20 of a subsystem group's health (slice G's kit: one patch = 20 group health, §18.3). */
export const GROUP_PART_HEALTH = 20;
export const QUOTE_TTL_S = 120;
export const OPERATOR_FEE_BPS = 800;
export const PLATFORM_FEE_BPS = 200;
export const MISSILE_REARM_SOV = 3;
export const COUNTER_REARM_SOV = 2;
/** A firm keeps its own reserve before it sells parts: a yard one hull's ore, a miner one run's. */
export function firmPartReserve(kind: string): number { return kind === 'yard' ? HULL_ORE_U : kind === 'miner' ? SHIPMENT_LOT_U : 0; }
export type PartsSource = 'firm' | 'relief' | 'own';
export interface RepairIn {
  stationKey: string;
  hpMax: number; missingHp: number;          // missingHp is the client's CLAIM, clamped here to the fit
  missilesMissing: number; countersMissing: number;
  missilesMax: number; countersMax: number;
  source: PartsSource; partUnitSov: bigint;  // the supplier's price per part (0 for own parts)
  mustered: boolean;                          // a mustered pilot at a fort: labour waived
  /** Slice G's subsystem groups: the health missing from engine + weapons on the SERVER's row (0 = none / no row). */
  groupMissing: number;
}
export interface RepairQuote {
  restoreHp: number; parts: number; partsSov: bigint; labourSov: bigint; rearmSov: bigint; operatorFeeSov: bigint;
  platformFeeSov: bigint; totalSov: bigint; durationS: number; missiles: number; counters: number;
  capped: boolean; capHp: number; nothing: boolean;
  /** The groups this job restores to full (a frontier outpost's limited repair patches the hull only). */
  groupParts: number; restoresGroups: boolean;
}
const bpsOf = (v: bigint, bps: number): bigint => (v * BigInt(bps)) / 10_000n;
const atLeast1 = (fee: bigint, base: bigint): bigint => (base > 0n && fee < 1n ? 1n : fee);
export function repairQuote(r: RepairIn): RepairQuote {
  const svc = roleService(r.stationKey);
  const role = stationRoleOf(r.stationKey);
  const hpMax = Math.max(1, Math.floor(r.hpMax));
  const missing = Math.max(0, Math.min(hpMax, Math.floor(r.missingHp)));
  const current = hpMax - missing;
  const capHp = Math.floor((hpMax * svc.restoreCapPct) / 100);
  const target = Math.max(current, Math.min(hpMax, capHp));
  const restoreHp = Math.max(0, target - current);
  const capped = restoreHp < missing;
  const restoresGroups = svc.restoreCapPct >= 100 && r.groupMissing > 0;
  const groupParts = restoresGroups ? Math.ceil(Math.max(0, r.groupMissing) / GROUP_PART_HEALTH) : 0;
  const parts = Math.ceil(restoreHp / PART_HP) + groupParts;
  const missiles = Math.max(0, Math.min(r.missilesMax, Math.floor(r.missilesMissing)));
  const counters = Math.max(0, Math.min(r.countersMax, Math.floor(r.countersMissing)));
  const partsSov = r.source === 'own' ? 0n : BigInt(parts) * r.partUnitSov;
  const waived = r.mustered && role === 'military_fort';
  const labourSov = waived ? 0n : BigInt(parts * svc.labourPerPartSov);
  const rearmSov = BigInt(missiles * MISSILE_REARM_SOV + counters * COUNTER_REARM_SOV);
  const opBase = partsSov + labourSov;
  const operatorFeeSov = atLeast1(bpsOf(opBase, OPERATOR_FEE_BPS), opBase);
  const pre = opBase + rearmSov + operatorFeeSov;
  const platformFeeSov = atLeast1(bpsOf(pre, PLATFORM_FEE_BPS), pre);
  const totalSov = pre + platformFeeSov;
  const durationS = Math.min(40, 6 + 2 * parts + (missiles + counters > 0 ? 2 : 0));
  return {
    restoreHp, parts, partsSov, labourSov, rearmSov, operatorFeeSov, platformFeeSov, totalSov, durationS, missiles, counters, capped, capHp,
    nothing: restoreHp <= 0 && missiles + counters <= 0 && groupParts <= 0, groupParts, restoresGroups,
  };
}

// ── THE SETTLEMENT (the §19.6 shape: escrow → parts bought → labour / fees paid; one posting each) ───────────────
export interface Leg { account: string; delta: bigint }
/** Sum legs per account, drop zeros — a posting never carries a zero leg or one account twice. */
export function aggregateLegs(legs: readonly Leg[]): Leg[] {
  const m = new Map<string, bigint>();
  for (const l of legs) m.set(l.account, (m.get(l.account) ?? 0n) + l.delta);
  const out: Leg[] = [];
  for (const [account, delta] of m) if (delta !== 0n) out.push({ account, delta });
  return out;
}
export interface ServiceParties { customer: string; supplier: string; engineer: string; operator: string; platform: string }
export interface ServiceMoney { totalSov: bigint; partsSov: bigint; labourSov: bigint; operatorFeeSov: bigint; platformFeeSov: bigint; rearmSov: bigint }
/** Acceptance: the customer's escrow, then the parts bought from the supplier (none when they are the customer's own). */
export function acceptPostings(p: ServiceParties, m: ServiceMoney): Leg[][] {
  const out: Leg[][] = [aggregateLegs([{ account: p.customer, delta: -m.totalSov }, { account: p.platform, delta: m.totalSov }])];
  const buy = aggregateLegs([{ account: p.platform, delta: -m.partsSov }, { account: p.supplier, delta: m.partsSov }]);
  if (buy.length >= 2) out.push(buy);
  return out.filter((t) => t.length >= 2);
}
/** Completion: labour to the engineer, the fee to the operator; the platform fee and the munitions stay. */
export function completePosting(p: ServiceParties, m: ServiceMoney): Leg[] {
  return aggregateLegs([
    { account: p.platform, delta: -(m.labourSov + m.operatorFeeSov) },
    { account: p.engineer, delta: m.labourSov },
    { account: p.operator, delta: m.operatorFeeSov },
  ]);
}
/** Cancellation (or a target that is gone): everything not yet earned comes back; the BOUGHT parts are the
 *  customer's goods (never both the parts' money and the parts). */
export function cancelSplit(m: ServiceMoney): { refundSov: bigint; keptPartsSov: bigint } {
  return { refundSov: m.totalSov - m.partsSov, keptPartsSov: m.partsSov };
}
export function cancelPosting(p: ServiceParties, m: ServiceMoney): Leg[] {
  const { refundSov } = cancelSplit(m);
  return aggregateLegs([{ account: p.platform, delta: -refundSov }, { account: p.customer, delta: refundSov }]);
}

// ── THE OUTAGES (a service row's verdict — the deciding numbers, never a client guess) ───────────────────────────
export type Outage = '' | 'awaiting_parts' | 'unfunded' | 'berth_occupied' | 'worker_stale';
export const WORKER_STALE_S = 120;
export const OUTAGE_LABEL: Readonly<Record<Exclude<Outage, ''>, string>> = {
  awaiting_parts: 'awaiting parts', unfunded: 'unfunded', berth_occupied: 'berth occupied', worker_stale: 'worker stale',
};
export interface ServiceFacts { berths: number; busy: number; partsAvail: number; source: 'firm' | 'relief'; reliefBudgetLeft: number; sweepAgeS: number }
/** The station's service verdict: the first outage in a fixed order (stale worker, berths, unfunded relief, parts). */
export function serviceOutage(f: ServiceFacts): Outage {
  if (f.sweepAgeS > WORKER_STALE_S) return 'worker_stale';
  if (f.busy >= f.berths) return 'berth_occupied';
  if (f.partsAvail <= 0 && f.source === 'relief' && f.reliefBudgetLeft <= 0) return 'unfunded';
  if (f.partsAvail <= 0) return 'awaiting_parts';
  return '';
}
/** Can THIS repair start: the berth, then the parts the quote needs (own parts never wait on the station). */
export function repairProblem(f: { berths: number; busy: number; partsNeeded: number; partsAvail: number; source: PartsSource; ownAvail: number; reliefBudgetLeft: number }): string | null {
  if (f.busy >= f.berths) return `berth occupied — all ${f.berths} cradle${f.berths === 1 ? ' is' : 's are'} working`;
  if (f.source === 'own') return f.ownAvail >= f.partsNeeded * PART_ORE_U ? null : `awaiting parts — you hold ${f.ownAvail} u of ferrite ore here; the job needs ${f.partsNeeded * PART_ORE_U} u`;
  if (f.partsAvail >= f.partsNeeded) return null;
  if (f.source === 'relief' && f.reliefBudgetLeft <= 0 && f.partsAvail <= 0) return 'unfunded — the relief programme\'s day budget is spent';
  return `awaiting parts — the station holds ${f.partsAvail} part${f.partsAvail === 1 ? '' : 's'}; the job needs ${f.partsNeeded}`;
}

// ── THE RELIEF PROGRAMME (an explicit, bounded platform subsidy — never a goods faucet: relief parts become HP) ──
export const RELIEF_BUDGET_PER_DAY = 600;
/** Whole-hour restock of a station's relief stock, bounded by its cap and the day's remaining budget. */
export function reliefRestock(stock: number, cap: number, perHour: number, hours: number, budgetLeft: number): { stock: number; used: number } {
  const want = Math.max(0, Math.min(cap - stock, perHour * Math.max(0, Math.floor(hours))));
  const used = Math.max(0, Math.min(want, budgetLeft));
  return { stock: stock + used, used };
}

// ── THE WRECK BERTH (the salvage yard's difference) ──────────────────────────────────────────────────────────────
export const RECOVERY_BASE_SOV = 25;
export const RECOVERY_PER_U_SOV = 2;
export const RECOVERY_DURATION_S = 20;
export function recoveryQuote(units: number): { feeSov: bigint; durationS: number } {
  const u = Math.max(0, Math.floor(units));
  return { feeSov: BigInt(RECOVERY_BASE_SOV + RECOVERY_PER_U_SOV * u), durationS: RECOVERY_DURATION_S };
}
/** The rights: a salvage yard, the wreck in its system and not faded, and goods that are YOURS (the port's crates and
 *  another owner's lots are never yours to recover; a stripper's take left the wreck). */
export function recoveryProblem(f: { role: StationRole; wreckSystem: string; stationSystem: string; expired: boolean; yourUnits: number }): string | null {
  if (f.role !== 'salvage_yard') return 'Only a salvage yard has a wreck berth';
  if (f.expired) return 'That wreck has faded';
  if (f.wreckSystem !== f.stationSystem) return 'The yard\'s tug works this system only';
  if (f.yourUnits <= 0) return 'Nothing of yours is left in that wreck';
  return null;
}

// ── THE MUSTER (the fort's difference) ───────────────────────────────────────────────────────────────────────────
export function musterProblem(f: { role: StationRole; sworn: string; already: boolean }): string | null {
  if (f.role !== 'military_fort') return 'The muster board is at a fort';
  if (!f.sworn) return 'Swear to a civilisation first (the Duty tab)';
  if (f.already) return 'You are on this war\'s roster already';
  return null;
}

// ── THE DEPOT (a capped safe-dock subsidy under the flag) ────────────────────────────────────────────────────────
export const DEPOT_RANGE_M = 600;
export const DEPOT_COOLDOWN_S = 600;
export function depotWaitS(lastAtS: number, nowS: number): number { return Math.max(0, Math.ceil(lastAtS + DEPOT_COOLDOWN_S - nowS)); }

// ── THE BOUNDED FIRM PLANNER (report §20.1) ──────────────────────────────────────────────────────────────────────
export const PLAN_MAX_CANDIDATES = 8;
export const PLAN_MAX_BIDS = 5;
export const BID_MIN_U = 20;
export const DELAY_SOV_PER_MIN = 2;
export const TURNAROUND_MIN = 5;
/** A firm never bets more than this share of its own purse on one run (the expected loss). */
export const EXPOSURE_SHARE = 0.25;
export const YARD_LIQUIDITY_SHARE = 0.25;
export const FILL_TAX_BPS = 500; // = space_catalog ORDER_FEES.fillTaxBps (the seller pays it at the fill)
/** The lane's loss probability from observed runs (a two-run prior at the unescorted odds) with an interval. */
export function laneLoss(lost: number, arrived: number): { p: number; lo: number; hi: number; n: number; weak: boolean } {
  const n = Math.max(0, lost) + Math.max(0, arrived);
  const prior = 2 * AMBUSH_P_UNESCORTED;
  const est = (k: number): number => Math.max(0.02, Math.min(0.9, (k + prior) / (n + 2)));
  const weak = n < 5;
  return { p: est(lost), lo: est(Math.max(0, lost - 1)), hi: est(lost + 1), n, weak };
}
export function escortOdds(escorted: number, runs: number): number { return Math.max(0, Math.min(1, (escorted + 0.4) / (Math.max(0, runs) + 2))); }
export type CandKind = 'own_yard' | 'treaty_yard' | 'bid' | 'hold';
export interface PlanCandidateIn {
  id: string; kind: CandKind; label: string; toKey: string; qty: number;
  /** Sale per unit if delivered (a bid's resting price; a yard's base value per unit). */
  unitSov: bigint;
  /** A yard pays min(its cash, the value) — the documented short-pay (none for a bid: its escrow is held). */
  buyerCashSov: bigint | null;
  flightS: number;
  /** Facts the filters read. */
  yardFull?: { hulls: number; ore: number };
  bidLeft?: number; bidOwnedByFirm?: boolean; bidExpiresInS?: number;
  treatyOpen?: boolean;
}
export interface PlanIn {
  stock: number; lotU: number; paused: string; ownYardSupplied: boolean;
  pLoss: number; pEscort: number; escortRewardSov: bigint; firmCashSov: bigint;
  candidates: readonly PlanCandidateIn[];
}
export interface PlanScore { saleSov: number; feesSov: number; escortSov: number; hullLossSov: number; delaySov: number; netSov: number; perMin: number; valueSov: number }
export interface PlanRow { id: string; kind: CandKind; label: string; toKey: string; qty: number; stop: string; score: PlanScore | null }
const zeroScore: PlanScore = { saleSov: 0, feesSov: 0, escortSov: 0, hullLossSov: 0, delaySov: 0, netSov: 0, perMin: 0, valueSov: 0 };
/** THE §20.1 FORMULA, literally: expected net = scenario-weighted realized sale − acquisition − scenario-weighted
 *  fees / freight / escort − disjoint expected uninsured hull / repair loss − delay cost. The cargo-loss probability
 *  enters ONCE — in the sale's scenario weight — and never again as a lost-basis penalty. */
export function expectedNet(f: { saleIfDelivered: number; pLoss: number; acquisition: number; feesIfDelivered: number; escortIfDelivered: number; hullLoss: number; delay: number }): number {
  const delivered = 1 - Math.max(0, Math.min(1, f.pLoss));
  return Math.round(delivered * f.saleIfDelivered) - f.acquisition - Math.round(delivered * (f.feesIfDelivered + f.escortIfDelivered)) - f.hullLoss - f.delay;
}
/** §20.1: expected net = scenario-weighted sale (the loss counted ONCE, there) − fees − escort − hull loss − delay,
 *  ranked per occupied ship-minute. Integer credits. */
export function scoreCandidate(c: PlanCandidateIn, p: Pick<PlanIn, 'pLoss' | 'pEscort' | 'escortRewardSov'>): PlanScore {
  if (c.kind === 'hold') return zeroScore;
  const gross = Number(c.unitSov) * c.qty;
  const value = c.buyerCashSov === null ? gross : Math.min(gross, Number(c.buyerCashSov));
  const delivered = 1 - p.pLoss;
  const saleSov = Math.round(delivered * value);
  const feesSov = c.kind === 'bid' ? Math.round(delivered * Math.floor((gross * FILL_TAX_BPS) / 10_000)) : 0;
  const escortSov = Math.round(p.pEscort * delivered * Number(p.escortRewardSov));
  const hullLossSov = 0; // the lane's freighter is not an asset on any ledger — no uninsured hull to lose
  const minutes = c.flightS / 60;
  const delaySov = Math.round(minutes * DELAY_SOV_PER_MIN);
  // = expectedNet(…) with acquisition 0 (the miner's own extraction), each scenario-weighted term rounded on its own.
  const netSov = saleSov - feesSov - escortSov - hullLossSov - delaySov;
  return { saleSov, feesSov, escortSov, hullLossSov, delaySov, netSov, perMin: netSov / Math.max(1, minutes + TURNAROUND_MIN), valueSov: value };
}
/** The legal filters, in order — the first that fails is the candidate's stop reason ('' = it qualifies). */
export function candidateStop(c: PlanCandidateIn, p: PlanIn): string {
  if (c.kind === 'hold') return '';
  if (p.paused) return p.paused;
  if (p.stock < c.qty) return `only ${p.stock} u of ore at the firm's station (a run is ${c.qty} u)`;
  if (c.kind === 'bid' && c.bidOwnedByFirm) return 'the bid is the firm\'s own';
  if (c.yardFull && c.yardFull.hulls >= HULLS_MAX && c.yardFull.ore >= 2 * HULL_ORE_U) return `the yard is full and stocked (${c.yardFull.hulls}/${HULLS_MAX} hulls, ${c.yardFull.ore} u of ore)`;
  if (c.kind === 'bid' && (c.bidLeft ?? 0) < BID_MIN_U) return `the bid wants only ${c.bidLeft ?? 0} u (a run carries ${BID_MIN_U}+)`;
  if (c.buyerCashSov !== null) {
    const gross = c.unitSov * BigInt(c.qty);
    if (c.buyerCashSov * 10_000n < gross * BigInt(Math.round(YARD_LIQUIDITY_SHARE * 10_000))) return `the yard holds ${c.buyerCashSov} ⓒ — not a quarter of the run's ${gross} ⓒ`;
  }
  if (c.kind === 'bid' && (c.bidExpiresInS ?? 0) < c.flightS + 600) return 'the bid expires before the run could land';
  if (c.kind === 'treaty_yard' && !c.treatyOpen) return 'no standing ceasefire (hand-in access is a treaty\'s permission)';
  if ((c.kind === 'treaty_yard' || c.kind === 'bid') && !p.ownYardSupplied) return 'the civ\'s own yard is short — its needs come first';
  const s = scoreCandidate(c, p);
  if (p.pLoss * s.valueSov > Number(p.firmCashSov) * EXPOSURE_SHARE) return `the expected loss (${Math.round(p.pLoss * s.valueSov)} ⓒ) is over a quarter of the firm's purse`;
  if (s.netSov <= 0) return `no positive expected net (${s.netSov} ⓒ)`;
  return '';
}
/** The deterministic choice: the best qualifying candidate per ship-minute (ties to the candidate id); else HOLD with
 *  every candidate's stop reason. ≤ PLAN_MAX_CANDIDATES are read — never more work than the bound. */
export function planFirm(p: PlanIn): { chosen: string; rows: PlanRow[]; stopReason: string } {
  const cands = p.candidates.slice(0, PLAN_MAX_CANDIDATES);
  const rows: PlanRow[] = cands.map((c) => {
    const stop = candidateStop(c, p);
    return { id: c.id, kind: c.kind, label: c.label, toKey: c.toKey, qty: c.qty, stop, score: c.kind === 'hold' ? zeroScore : scoreCandidate(c, p) };
  });
  const ok = rows.filter((r) => r.kind !== 'hold' && r.stop === '');
  ok.sort((a, b) => (b.score!.perMin - a.score!.perMin) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  if (ok.length) return { chosen: ok[0].id, rows, stopReason: '' };
  const why = rows.filter((r) => r.kind !== 'hold').map((r) => `${r.label}: ${r.stop}`);
  return { chosen: rows.find((r) => r.kind === 'hold')?.id ?? 'hold', rows, stopReason: why.length ? why.join(' · ') : 'no candidate' };
}

// ── THE TREATY'S ONE PERMISSION (report §20.1 last ¶) ────────────────────────────────────────────────────────────
export interface HandInRoute { minerFirmId: bigint; miner: string; fromKey: string; yardFirmId: bigint; yard: string; toKey: string }
/** A STANDING ceasefire grants HAND-IN ACCESS: each civ's miner may deliver its surplus ore to the OTHER civ's yard. */
export const TREATY_PERMISSION = {
  kind: 'ceasefire',
  key: 'yard_hand_in',
  label: 'Hand-in access',
  text: 'Each civ\'s miner may deliver its surplus ore to the other civ\'s yard (only while its own yard is supplied).',
  cycles: CEASEFIRE_CYCLES,
} as const;
export function handInRoutes(): HandInRoute[] {
  const out: HandInRoute[] = [];
  for (const m of FIRM_DEFS) {
    if (m.kind !== 'miner') continue;
    const y = FIRM_DEFS.find((f) => f.kind === 'yard' && f.civ !== m.civ);
    if (y) out.push({ minerFirmId: m.firmId, miner: m.name, fromKey: m.stationKey, yardFirmId: y.firmId, yard: y.name, toKey: y.stationKey });
  }
  return out;
}
/** What the permission means for a treaty row at this cycle: open now, from / until (cycles), or not (and why). */
export function treatyPermissionState(t: { state: string; fromCyc: number; untilCyc: number } | null, cyc: number): { open: boolean; line: string } {
  if (!t) return { open: false, line: 'no treaty — the yards take only their own civ\'s ore' };
  if (t.state === 'standing' && t.fromCyc <= cyc && t.untilCyc > cyc) return { open: true, line: `${TREATY_PERMISSION.label} OPEN — ${t.untilCyc - cyc} war cycle${t.untilCyc - cyc === 1 ? '' : 's'} left (until cycle ${t.untilCyc})` };
  if (t.state === 'standing') return { open: false, line: `${TREATY_PERMISSION.label} from cycle ${t.fromCyc} for ${t.untilCyc - t.fromCyc} cycles` };
  if (t.state === 'proposed') return { open: false, line: `proposed — if accepted: ${TREATY_PERMISSION.label} for ${TREATY_PERMISSION.cycles} war cycles` };
  return { open: false, line: `the treaty is ${t.state} — no hand-in access` };
}
